import { head } from 'lodash';
import { getRoot } from 'mobx-easy';
import type { FilterTypes } from 'react-table';
import { AlertOwnerEnum } from 'bringg-web/types/common.consts';

import type { BaseRecord } from 'bringg-web/components/bringg-react-table';
import RootStore from '../../stores/root-store';
import { EXCEPTIONS_COLUMN_IDS } from '../../consts';

const getFilterTypes = <T extends BaseRecord>(): FilterTypes<T> => ({
	arrayIncludes: (rows, filterIds, values) => {
		const key = head(filterIds);
		// Be aware can behave unpredictable if !Array.isArray(values)
		const valuesSet = new Set(values);
		return rows.filter(row => valuesSet.has(row.original[key]));
	},
	boolean: (rows, filterIds, value: boolean) => {
		const key = head(filterIds);
		return rows.filter(row => Boolean(row.original[key]) === value);
	},
	// Custom team_ids filter logic
	teamIdWithChildren: (rows, filterIds, teamIds: number[]) => {
		const key = head(filterIds);
		if (key !== EXCEPTIONS_COLUMN_IDS.TEAM_ID) {
			return rows;
		}

		const { get: getTeamById } = getRoot<RootStore>().data.teamsStore;
		// We need to handle team's parent-child relations
		// if enabled by filters team has children teams we also need to show them in filtered result
		// teamIdsSet stands to hold teamIds + childrenTeamIds
		const teamIdsSet = new Set(
			teamIds.concat(
				teamIds.reduce((acc, id) => {
					const team = getTeamById(id);
					return team?.childrenTeamIds.length ? acc.concat(team.childrenTeamIds) : acc;
				}, [])
			)
		);

		return rows.filter(row => teamIdsSet.has(row.original[key] as number));
	},
	alertOwners: (rows, filterIds, value: AlertOwnerEnum) => {
		const {
			currentUser: { id: currentUserId }
		} = getRoot<RootStore>().data.usersStore;

		if (value === AlertOwnerEnum.CURRENT_USER) {
			return rows.filter(row => row.original.owner_id === currentUserId);
		}
		if (value === AlertOwnerEnum.OTHER) {
			return rows.filter(row => row.original.owner_id && row.original.owner_id !== currentUserId);
		}
		if (value === AlertOwnerEnum.NO_OWNER) {
			return rows.filter(row => !row.original.owner_id);
		}

		return rows;
	},
	taskIds: (rows, filterIds, value: string) => {
		const key = head(filterIds) as string;
		if (key !== EXCEPTIONS_COLUMN_IDS.EXTERNAL_ID) {
			return rows;
		}

		const { alertsStore } = getRoot<RootStore>().data;

		return rows.filter(row => {
			const alert = alertsStore.get(row.id);
			const extraTaskIds = alert?.extra_data.task_ids as number[];
			const alertRelatedIds = extraTaskIds?.length ? extraTaskIds : [alert.task_id];

			return alertRelatedIds?.join().includes(value);
		});
	},
	companies: (rows, filterIds, value: number[]) => {
		const key = head(filterIds) as string;
		if (key !== EXCEPTIONS_COLUMN_IDS.COMPANY_ID) {
			return rows;
		}

		const { alertsStore } = getRoot<RootStore>().data;

		return rows.filter(row => value.includes(alertsStore.get(row.id)[key]));
	}
});

export default getFilterTypes;
